
import {
  defineComponent,
  reactive,
  computed,
  ref,
  onMounted,
} from 'vue';
import TextField from '@/components/inputs/TextField.vue';
import TextareaField from '@/components/inputs/TextareaField.vue';
import NumberField from '@/components/inputs/NumberField.vue';
import DateField from '@/components/inputs/DateField.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCalendarAlt, faSpinner, faCheck } from '@fortawesome/pro-light-svg-icons';

library.add(faCalendarAlt);
library.add(faSpinner);
library.add(faCheck);

export default defineComponent({
  emits: [
    'update:modelValue',
    'input',
  ],

  components: {
    FontAwesomeIcon,
    TextField,
    NumberField,
    DateField,
    TextareaField,
  },

  props: {
    type: {
      type: String,
      required: false,
      default: () => 'text',
    },
    modelValue: {},
    name: {
      type: String,
      required: false,
      default: () => null,
    },
    label: {
      type: String,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    options: {
      type: Array,
      required: false,
      default: () => [],
    },
    error: {
      type: [String, Array],
      required: false,
      default: () => '',
    },
    minDate: [String, Number, Date],
    maxDate: [String, Number, Date],
    minLength: {
      type: Number,
      required: false,
      default: () => null,
    },
    maxLength: {
      type: Number,
      required: false,
      default: () => null,
    },
    min: {
      type: Number,
      required: false,
      default: () => null,
    },
    max: {
      type: Number,
      required: false,
      default: () => null,
    },
    onlyNumeric: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    errorNumeric: {
      type: String,
      required: false,
      default: () => null,
    },
    showLoader: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    showValidCheck: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    disableWeekend: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    iconRight: {
      type: String,
      required: false,
      default: () => null,
    },
    iconLeft: {
      type: String,
      required: false,
      default: () => null,
    },
    borderless: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    rounded: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    hideLabel: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    preventPaste: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    autocomplete: {
      type: String,
      required: false,
      default: () => 'on',
    },
  },

  setup(props, context) {
    const ucFirst = ($string: string) => $string.charAt(0).toUpperCase() + $string.slice(1);

    const component = reactive({
      id: `if-${Math.random().toString(36).substr(2, 9)}`,
      currentComponent: `${ucFirst(props.type)}Field`,
      error: props.error || '',
    });

    const input = ref();

    const value = computed({
      get(): unknown {
        return props.modelValue;
      },
      set(newValue: unknown): void {
        context.emit('update:modelValue', newValue);
      },
    });

    const hasError = computed(() => {
      const compError = `${Array.isArray(component.error) && component.error[0] ? component.error[0] : component.error}`.trim();
      const $error = compError.length ? compError : (props.error || '');

      return $error !== null
        ? $error.length > 0
        : false;
    });

    const errorMessage = computed(() => {
      if (hasError.value) {
        const compError = `${Array.isArray(component.error) && component.error[0] ? component.error[0] : component.error}`.trim();
        const $error = compError.length ? compError : props.error;

        return Array.isArray($error)
          ? $error[0]
          : $error;
      }

      return '';
    });

    const onPaste = (e: Event) => {
      if (props.preventPaste) {
        e.preventDefault();
      }
    };

    const emitInput = ($value: unknown) => {
      context.emit('input', $value);
    };

    const onInput = ($value: string | number) => {
      let formattedVal = $value;

      if (typeof formattedVal === 'string') {
        if (props.onlyNumeric) {
          formattedVal = formattedVal.replace(/\D/g, '');
        }

        if (props.minLength !== null && formattedVal.length < props.minLength) {
          component.error = `Mindestens ${props.minLength} Zeichen`;
          if (props.onlyNumeric) {
            component.error = props.errorNumeric || `Zeichenfolge aus mindestens ${props.minLength} Zahlen`;
          }
        } else if (props.maxLength !== null && formattedVal.length > props.maxLength) {
          formattedVal = formattedVal.substr(0, props.maxLength);
          if (input.value) {
            input.value.$el.value = formattedVal;
          }
        }
        if (props.minLength !== null && formattedVal.length >= props.minLength) {
          component.error = [];
        }
      }

      value.value = formattedVal;
      emitInput(formattedVal);
    };

    const onBlur = () => {
      let formattedVal = value.value;

      if (typeof formattedVal === 'string') {
        if (props.onlyNumeric) {
          let stringVal = formattedVal as string;

          stringVal = stringVal.replace(/\D/g, '');

          const numberValue = parseInt(stringVal, 10);

          if (input.value) {
            if (props.min !== null && numberValue < props.min) {
              formattedVal = `${props.min}`;
            }

            if (props.max !== null && numberValue > props.max) {
              formattedVal = `${props.max}`;
            }

            input.value.$el.value = formattedVal;
          } else if (props.min !== null) {
            input.value.$el.value = formattedVal;
          }
        }
      } else if (input.value) {
        const numberValue = formattedVal as number;

        if (props.min !== null && numberValue < props.min) {
          formattedVal = props.min;
        }

        if (props.max !== null && numberValue > props.max) {
          formattedVal = props.max;
        }

        input.value.$el.value = formattedVal;
      }

      value.value = formattedVal;
      emitInput(formattedVal);
    };

    const focused = ref(false);
    const filled = computed(() => `${typeof value.value === 'number' ? value.value : value.value || ''}`.trim() !== '');

    onMounted(() => {
      emitInput(props.modelValue);
    });

    return {
      hasError,
      errorMessage,
      ucFirst,
      component,
      input,
      onInput,
      onBlur,
      focused,
      filled,
      value,
      onPaste,
    };
  },
});
